/**
 * 소재 상세가 **글 목록 화면에서** 더 싣는 것 — 검색어 형광 · 「캠페인 추가」 · 지표 기준.
 *
 * 소재 찾기·허브는 아무것도 안 넘긴다(기본값). 게시물 탭(`moment-content` — 검색 · 키워드 모니터링 ·
 * 브랜드 멘션)이 제 조건을 이 꼴로 묶어 네 보기 모두의 상세에 같은 값을 준다.
 */

/**
 * 수치를 어느 시점으로 읽나.
 * `sort` = 정렬 스냅샷(`metricsSort` — 목록 정렬·집계와 같은 값) · `latest` = 가장 최근 수집분.
 * 값이 없으면 색인 루트 값(`viewCount` 등)을 그대로 쓴다.
 */
export type MetricBasis = 'sort' | 'latest';

/** 본문·이름에 칠할 말 — `highlightTextWithHtmlRemoval` 의 인자 그대로다(쉼표로 여러 개) */
export type DetailHighlight = {
  searchKeyword: string;
  clientSearch: string;
  topicKeywords: string[];
};

export type MediaDetailOptions = {
  /** 칠할 말 — `null` 이면 본문을 글자 그대로 그린다 */
  highlight?: DetailHighlight | null;
  /** 「캠페인 추가」를 발에 세우나(브랜드 멘션) */
  campaignAdd?: boolean;
  metricBasis?: MetricBasis;
  /** 발의 북마크를 감추나 — 폴더 다중 선택 중인 목록(`isBookmark`)이 켠다 */
  hideBookmark?: boolean;
};

/** 말 하나 또는 여럿 → 쉼표로 이은 한 줄(빈 말은 버린다) */
function joinTerms(value: unknown): string {
  const list = Array.isArray(value) ? value : [value];
  return list.map((v) => (typeof v === 'string' ? v.trim() : '')).filter(Boolean).join(',');
}

/**
 * 칠할 말 묶음 — 셋 다 비면 `null`.
 * @param input 화면이 쥔 검색어(문자열·배열) · 결과 내 재검색어 · 주제 키워드
 */
export function toDetailHighlight(input: {
  searchKeyword?: unknown;
  clientSearch?: unknown;
  topicKeywords?: readonly unknown[] | null;
}): DetailHighlight | null {
  const searchKeyword = joinTerms(input.searchKeyword);
  const clientSearch = joinTerms(input.clientSearch);
  const topicKeywords = (input.topicKeywords ?? []).map((v) => (typeof v === 'string' ? v.trim() : '')).filter(Boolean);
  if (!searchKeyword && !clientSearch && topicKeywords.length === 0) return null;
  return { searchKeyword, clientSearch, topicKeywords };
}

/**
 * 글 목록 부품의 props(`PostFeed`·`PostTable`·글 상세와 같은 이름) → 상세 옵션.
 * 🔑 글 목록은 수치를 늘 시점으로 읽는다 — 스위치가 켜지면 정렬 스냅샷, 꺼지면 가장 최근 수집분.
 */
export function toPostListDetailOptions(props: {
  searchKeyword?: unknown;
  clientSearch?: unknown;
  topicKeywords?: readonly unknown[] | null;
  enableCampaignAdd?: boolean;
  detailUsesMetricDetailSort?: boolean;
  isBookmark?: boolean;
}): MediaDetailOptions {
  return {
    highlight: toDetailHighlight(props),
    campaignAdd: props.enableCampaignAdd === true,
    metricBasis: props.detailUsesMetricDetailSort ? 'sort' : 'latest',
    hideBookmark: props.isBookmark === true,
  };
}

/**
 * 게시물 탭 → 상세 옵션. 네 보기(섬네일·격자·목록·표)가 같은 값을 받는다.
 * @param posts 게시물 탭 슬롯 — 검색어 셋 · `detailUsesMetricDetailSort` · `extra.enableCampaignAdd`
 */
export function toMomentDetailOptions(posts: {
  searchKeyword?: unknown;
  clientSearch?: unknown;
  topicKeywords?: readonly unknown[] | null;
  detailUsesMetricDetailSort?: boolean;
  extra?: Record<string, unknown> | null;
}): MediaDetailOptions {
  return toPostListDetailOptions({ ...posts, enableCampaignAdd: posts.extra?.enableCampaignAdd === true });
}
